import React, { useEffect, useMemo, useState } from 'react'

import { Box, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

import RBIAutocomplete from '../../RBIAutocomplete/RBIAutocomplete';
import { TreeSelector } from '../../TreeSelector';
import { fetchBrandReportingUnits, fetchReportingUnitGroupRestaurants, fetchReportingUnitGroups, fetchReportingUnitGroupsCountries } from '../../../redux/actions/reportingUnitsActions';
import { getFranchiseGroups, getReportingUnitById, getReportingUnitsByBrand } from '../../../redux/selectors/reportingUnitsSelectors';

export default function ShowCorporateRestaurantList({ value,
    onChange = (values: any[]) => { }, }) {
    const dispatch: any = useDispatch();

    const [selectedReportingUnit, setSelectedReportingUnit] = useState<any>({});
    const [selectedFranchiseGroup, setSelectedFranchiseGroup] = useState<any>({});
    const [selectedCountry, setSelectedCountry] = useState<any>({});
    const [filterValue, setFilterValue] = useState('');
    const [filterDelayedValue, setFilterDelayedValue] = useState('');
    const selectedBrand = useSelector(
        (state: RootStateOrAny) => state.UIReducer.selectedBrand,
    );

    const [filterTimer, setFilterTimer] = useState<any>(null);
    const reportingUnits = useSelector(getReportingUnitsByBrand(selectedBrand));
    const countries = useSelector((state: RootStateOrAny) => state.ReportingUnitsReducer?.reportingUnitsCountries)
    const reportingUnit = useSelector(
        getReportingUnitById(selectedBrand, selectedReportingUnit?.id),
    );
    const franchiseGroups = useSelector(getFranchiseGroups(selectedBrand));

    const franchiseGroupsOptions = useMemo(() => {
        let options = [];
        if (reportingUnit?.groups) {
            options = reportingUnit.groups;
        }
        return options;
    }, [reportingUnit?.groups]);

    const restaurantsNodes = useMemo(() => {
        let nodes = null;
        const franchiseGroup = franchiseGroups[selectedFranchiseGroup.id];
        if (franchiseGroup?.restaurants) {
            nodes = franchiseGroup?.restaurants.map(restaurant => ({
                text: `# ${restaurant.number} | ${restaurant.name}`,
                path: `/${selectedBrand}/${reportingUnit.id}/${franchiseGroup.id}/${restaurant.id}`,
                type: 'Restaurant',
                hasChildren: false,
                number: restaurant.number,
                metas: {
                    groupId: selectedReportingUnit.id,
                    reportUnitId: reportingUnit.id,
                    reportUnitName: reportingUnit.name,
                    restaurantId: restaurant.id,
                },
            }));
        }
        return nodes;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFranchiseGroup, franchiseGroups]);

    const restaurantNodesPagination = useMemo(() => {
        let pagination = {};
        const franchiseGroup = franchiseGroups[selectedFranchiseGroup.id];

        if (franchiseGroup?.restaurants) {
            pagination = {
                ...franchiseGroup.restaurantsPagination,
                steps: 10,
            };
        }

        return pagination;
    }, [selectedFranchiseGroup, franchiseGroups]);

    const hasReportingUnitSelected = useMemo(
        () => !!selectedReportingUnit?.id,
        [selectedReportingUnit],
    );
    const hasCountrySelected = useMemo(
        () => !!selectedCountry?.id,
        [selectedCountry],
    );
    const hasFranchiseGroupSelected = useMemo(
        () => !!selectedFranchiseGroup?.id,
        [selectedFranchiseGroup],
    );
    const isLoadingRestaurants = useMemo(
        () => !restaurantsNodes,
        [restaurantsNodes],
    );

    useEffect(() => {
        if (dispatch && !reportingUnits)
            dispatch(fetchBrandReportingUnits(selectedBrand));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBrand, dispatch]);

    useEffect(() => {
        clearTimeout(filterTimer);
        setFilterTimer(
            setTimeout(() => {
                setFilterDelayedValue(filterValue);
            }, 500),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValue]);

    const handleReportingUnitChange = (name: string, selection: any) => {
        if (selection) {
            dispatch(fetchReportingUnitGroupsCountries(selectedBrand, selection.id));
        }
        setSelectedReportingUnit(selection || {});
        setSelectedCountry({});
        setSelectedFranchiseGroup({});
    };

    const handleCountryChange = (name: string, selection: any) => {
        if (selection) {
            dispatch(fetchReportingUnitGroups(selectedBrand, selectedReportingUnit.id, { country: selection.id }));
        }
        setSelectedCountry(selection || {});
        setSelectedFranchiseGroup({});
    }

    const handleFranchiseGroupChange = (name: string, selection: any) => {
        if (selection && !selection.restaurants) {
            dispatch(
                fetchReportingUnitGroupRestaurants(
                    selectedBrand,
                    selectedReportingUnit.id,
                    selection.id,
                    { limit: 100, country: selectedCountry.id },
                ),
            );
        }
        setSelectedFranchiseGroup(selection || {});
    };
    const handleFilterChange = ({ target }) => {
        setFilterValue(target.value);
    };
    const handleNodeSelectionChange = (values: any[]) => {
        onChange(values);
    };

    const handleFetchNextRestaurants = async (node: any) => {
        const { metas, pagination } = node || {};
        const { groupId, reportUnitId } = metas || {
            groupId: selectedFranchiseGroup.id,
            reportUnitId: selectedReportingUnit.id,
        };
        const {
            total,
            loadedCount,
            pageSize: limit,
            lastPage,
        } = pagination || restaurantNodesPagination;
        if (total > loadedCount) {
            await dispatch(
                fetchReportingUnitGroupRestaurants(
                    selectedBrand,
                    reportUnitId,
                    groupId,
                    { limit, page: lastPage + 1 },
                ),
            );
        }
    };

    const { t } = useTranslation();

    return (
        <Box>
            <Box
                style={{
                    padding: '5px 10px',
                }}
            >
                <RBIAutocomplete
                    id="lsReportingUnits"
                    label={t('productGroups.assignment.filterByReportingUnit')}
                    value={selectedReportingUnit}
                    options={reportingUnits || []}
                    width="100%"
                    style={{
                        backgroundColor: 'white',
                    }}
                    freeSolo={false}
                    onChangeInput={handleReportingUnitChange}
                    getOptionSelected={(option, value) => option.id === value.id}
                />
                <RBIAutocomplete
                    id="lsCountries"
                    label={t('productGroups.assignment.countries', 'Filter by Countries')}
                    value={selectedCountry}
                    options={countries || []}
                    width="100%"
                    style={{
                        backgroundColor: 'white',
                    }}
                    freeSolo={false}
                    disabled={!hasReportingUnitSelected}
                    onChangeInput={handleCountryChange}
                    getOptionSelected={(option, value) => option.id === value.id}
                />
                <RBIAutocomplete
                    id="lsFranchiseGroups"
                    label={t('productGroups.assignment.filterByFranchiseGroup')}
                    value={selectedFranchiseGroup}
                    options={franchiseGroupsOptions}
                    width="100%"
                    style={{
                        backgroundColor: 'white',
                    }}
                    freeSolo={false}
                    disabled={!hasCountrySelected}
                    onChangeInput={handleFranchiseGroupChange}
                    getOptionSelected={(option, value) => option.id === value.id}
                />
                {hasFranchiseGroupSelected && (
                    <TextField
                        size="small"
                        variant="outlined"
                        label={t('productGroups.assignment.filterByRestaurant')}
                        fullWidth
                        value={filterValue}
                        name="restaurantFilter"
                        onChange={handleFilterChange}
                        autoFocus={false}
                        disabled={isLoadingRestaurants}
                        style={{
                            marginTop: '8px',
                        }}
                    />
                )}
            </Box>
            <Box flexGrow={1}>
                {hasFranchiseGroupSelected && (
                    <TreeSelector
                        showStoreIcon={true}
                        watchForChange={[
                            selectedReportingUnit?.name,
                            selectedFranchiseGroup?.name,
                        ]}
                        nodes={restaurantsNodes}
                        value={value}
                        pagination={restaurantNodesPagination}
                        showLoadingOnRoot={true}
                        showNoDataOnRoot={true}
                        filterText={filterDelayedValue}
                        onChange={handleNodeSelectionChange}
                        onNextNodesPage={handleFetchNextRestaurants}
                    />
                )}
            </Box>
        </Box>
    );
}